import { get } from 'lodash';
import { QUICK_TEST_TYPE } from './VerifySharedConstants';
import { createAgentResultObject, getVerifyAgents } from './VerifyStrategyHelpers';
import verifyScriptedStrategy from './VerifyScriptedStrategy';

/**
 * Build the verify test payload for Web tests.
 * portal/ui source: verify.service.js buildWebVerifyTest()
 *
 * Web tests use a different payload from scripted tests:
 * - quickTestType: 'advanced' (not 'other')
 * - name: 'Verify ${url}' (not 'Verify ${testName}')
 * - testConfig passed directly (same as portal/ui's test.testConfig)
 *
 * @param {Object} formData - Current form values
 * @param {Object} testType - Test type configuration
 * @param {Array} agents - Available agents
 * @returns {Object} Payload to POST to /ipm/v1/admin/quicktests
 */
function buildVerifyPayload(formData, testType, agents) {
    const agentIds = getVerifyAgents(agents, formData, testType);

    return {
        name: `Verify ${formData.testConfig?.url || ''}`,
        type: testType._id,
        quickTestType: QUICK_TEST_TYPE.ADVANCED,
        beaconPackets: get(formData, 'beaconPackets', false),
        testConfig: formData.testConfig,
        adapterType: formData.adapterType,
        agents: { ids: agentIds },
        agentResults: agentIds.map(createAgentResultObject),
    };
}

/**
 * Strategy for Web test types.
 *
 * portal/ui architecture note: portal/ui's verify.service.js testTypeService() does NOT
 * map 'Web' to verifyWeb.service.js — Web tests fall through to VerifyScriptedTestService
 * for status checking, result display, and polling. Only the payload builder
 * (buildWebVerifyTest) is Web-specific.
 *
 * This strategy mirrors that design: Web-specific payload via buildVerifyPayload,
 * with all result handling delegated to the scripted strategy.
 *
 * @type {import('./ServiceTestVerifyHelper.js').VerifyStrategy}
 */
const verifyWebStrategy = {
    buildVerifyPayload,
    checkStatus: verifyScriptedStrategy.checkStatus,
    verifyHasResults: verifyScriptedStrategy.verifyHasResults,
    buildVerifyResult: verifyScriptedStrategy.buildVerifyResult,
    pollConfig: verifyScriptedStrategy.pollConfig,
    pollingCounter: verifyScriptedStrategy.pollingCounter,
};

export default verifyWebStrategy;
